import { compact, isNil, mapValues, sortBy } from 'lodash';
import { useMemo } from 'react';
import { Simplify } from 'type-fest';

import { useDevice } from '@/api/devices';
import {
    DissolvedOxygenDatum,
    PerSensor,
    PressureDatum,
    RawSensorData,
    SalinityDatum,
    TemperatureDatum,
    TurbidityDatum,
} from '@/api/historicalData';
import {
    consolidateBySensorPosition,
    convertUnit,
    renameSensorDatumKeys,
    useTransformQueryResult,
} from '@/api/historicalData/helpers';
import { SPL_BAND_SPL_READING_KEYS } from '@/api/historicalData/types';
import { useLiveSplitQuery } from '@/api/useLiveSplitQuery';
import { useDisplayPreferences } from '@/api/userAccount';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import { ExtendedPressureUnits, SalinityUnits, Units } from '@/common/convert';
import {
    CombinedQueriesResult,
    CombinedQueryOrQueriesResult,
    CombinedQueryResult,
    QueryConfig,
    SpotterIdOrIds,
} from '@/common/react-query';
import { QuantityType, unitInContext } from '@/components/data/Datum';
import { HISTORICAL_SENSOR_DATA_QUERY_KEY } from '@/config';
import { DateRange, useGlobalStore } from '@/store';
import { ChunkHours } from '@/utils';
import { addMockData, gapifyData, multiplySensorPositions } from '@/utils/mockDataHelpers';

export type GetHistoricalSensorDataParams = {
    spotterId: string;
    startDate: string;
    endDate: string;
    includeSoundPressureReadings?: boolean;
    useReadingsCache?: boolean;
    /**
     * Drops the per-second band rows from the response. Shapes what is handed back, not what is asked for:
     * the endpoint has no say in which sound-pressure rows a window contains, so this is applied here and
     * kept out of the request. Every row a caller receives has passed it, the mock ones included.
     */
    excludeSoundPressureBandReadings?: boolean;
};

export const getHistoricalSensorData = async (
    { excludeSoundPressureBandReadings, ...params }: GetHistoricalSensorDataParams,
    options?: RequestOptions
): Promise<RawSensorData[]> => {
    const mockDataSwitches = useGlobalStore.getState().mockDataSwitches;

    let { data } = await axios.get<RawSensorData[]>('/fetch/sensor-data', {
        params,
        ...requestConfig(options),
    });

    addMockData(data, params);

    /*
     * Sits between the mock rows arriving and the sensor positions being multiplied, so that the mock
     * hydrophone answers for the exclusion the same way a real one does and no copy is made of a row that
     * is about to go.
     */
    if (excludeSoundPressureBandReadings) {
        data = data.filter(({ data_type_name }) => !SPL_BAND_SPL_READING_KEYS.includes(data_type_name));
    }

    if (mockDataSwitches.multiSensor) {
        data.push(...multiplySensorPositions(data));
    }
    if (mockDataSwitches.gappyData) {
        // gapifyData builds its gap runs out of consecutive items, so it needs timestamp order. The response
        // itself is unordered; useRawSensorData is what orders it for everything downstream of here.
        data = gapifyData(sortBy(data, 'timestamp'));
    }

    return data;
};

/**
 * The widest settled chunk `/fetch/sensor-data` is asked for.
 *
 * The endpoint selects every location the spotter reported in the window, widens that window by an hour
 * either side, and eager-loads the smart mooring rows under a single `location_id in (...)`.
 * The cap is 65535 params in Postgres. A spotter reporting once a second puts 3600 locations in an hour, which this width keeps to under half the cap.
 */
export const SENSOR_DATA_MAX_CHUNK_HOURS: ChunkHours = 6;

export type UseHistoricalSensorDataOptions = {
    dateRange?: DateRange | null;
    includeSoundPressureReadings?: boolean;
    useReadingsCache?: boolean;
    /**
     * Removes the per-frequency sound-pressure reading rows before the query cache stores them, for a
     * consumer that reads only the broadband value.
     */
    excludeSoundPressureBandReadings?: boolean;
    /** A ceiling on the settled chunk width. Defaults to {@link SENSOR_DATA_MAX_CHUNK_HOURS}. */
    maxChunkHours?: ChunkHours;
    config?: Omit<QueryConfig<typeof getHistoricalSensorData>, 'select'>;
} & SpotterIdOrIds;

export function useRawSensorData(
    options: Simplify<Omit<UseHistoricalSensorDataOptions, 'spotterIds'>>
): CombinedQueryResult<RawSensorData[]>;

export function useRawSensorData(
    options: Simplify<Omit<UseHistoricalSensorDataOptions, 'spotterId'>>
): CombinedQueriesResult<RawSensorData[]>;

export function useRawSensorData({
    config,
    spotterId,
    spotterIds,
    dateRange,
    /*
     * Destructured so it stays out of `params`, which is spread into both the query key and the request
     * itself: this shapes the windows asked for, not what a window asks the endpoint for.
     */
    maxChunkHours = SENSOR_DATA_MAX_CHUNK_HOURS,
    excludeSoundPressureBandReadings,
    ...params
}: UseHistoricalSensorDataOptions): CombinedQueryOrQueriesResult<RawSensorData[]> {
    /*
     * A window carries band rows only when sound-pressure readings were asked for, so outside that the
     * exclusion has nothing to say and stays out of the key: a caller that asks for it unconditionally
     * still shares one cache entry with every other consumer of the same window.
     */
    const excludeBandReadings = Boolean(excludeSoundPressureBandReadings && params.includeSoundPressureReadings);

    return useLiveSplitQuery({
        queryKeyPrefix: HISTORICAL_SENSOR_DATA_QUERY_KEY,
        spotterId,
        spotterIds,
        dateRange,
        keyOptions: { ...params, ...(excludeBandReadings && { excludeSoundPressureBandReadings: true }) },
        fetchRange: ({ viewToken, signal, ...range }) =>
            getHistoricalSensorData(
                { ...range, ...params, excludeSoundPressureBandReadings: excludeBandReadings },
                { viewToken, signal }
            ),
        maxChunkHours,
        timestampKey: 'timestamp',
        sortOrder: 'asc',
        /*
         * /fetch/sensor-data selects locations for the window and joins each to its sensor samples, so the
         * samples come back grouped by an unordered location rather than by their own timestamps.
         * consolidateBySensorPosition and the gap fillers all require ascending order.
         */
        responseSortOrder: 'unsorted',
        config,
    });
}

export const useHistoricalSensorData = (options: UseHistoricalSensorDataOptions) => {
    return useTransformQueryResult(useRawSensorData(options), consolidateBySensorPosition);
};

export const useTemperatureData = (options: UseHistoricalSensorDataOptions) => {
    const { data: sensorData, ...response } = useHistoricalSensorDataByType('temperature', options);
    const { data: displayPreferences } = useDisplayPreferences();

    const data = useMemo(() => {
        if (!sensorData) return undefined;

        return mapValues(
            sensorData,
            (d) => convertUnit(d, 'temperature', displayPreferences?.temperature) as TemperatureDatum[]
        );
    }, [displayPreferences?.temperature, sensorData]);

    return { data, ...response };
};

export const usePressureData = (options: UseHistoricalSensorDataOptions) => {
    const { data: sensorData, ...response } = useHistoricalSensorDataByType('pressure', options);
    const { data: displayPreferences } = useDisplayPreferences();
    const { data: device } = useDevice({ spotterId: options.spotterId });

    const data = useMemo(() => {
        if (!sensorData) return undefined;
        const targetUnit = (unitInContext('pressure', displayPreferences?.pressure, device) ??
            'bar') as ExtendedPressureUnits;

        return mapValues(sensorData, (d) => convertUnit(d, 'pressure', targetUnit) as PressureDatum[]);
    }, [displayPreferences?.pressure, sensorData, device]);

    return { data, ...response };
};

export const useTurbidityData = (options: UseHistoricalSensorDataOptions) => {
    const { data: sensorData, ...response } = useHistoricalSensorDataByType('turbidity', options);

    return { data: sensorData as PerSensor<TurbidityDatum[]>, ...response };
};

export const useHistoricalSensorDataByType = <T extends QuantityType>(
    type: T,
    options: UseHistoricalSensorDataOptions
) => {
    const { data: sensorData, ...response } = useHistoricalSensorData(options);

    const data = useMemo(() => {
        if (!sensorData) return undefined;

        return mapValues(
            sensorData,
            (data) =>
                compact(
                    data.map((datum) => {
                        const sensorDatumValue = Object.values(datum.data).find((value) => value.unitType === type);

                        if (!sensorDatumValue) return null;

                        const { value, units } = sensorDatumValue;

                        if (isNil(value)) return null;

                        return {
                            timestamp: datum.timestamp,
                            [type]: value,
                            units,
                            unitType: type,
                        };
                    })
                ) as ({ timestamp: string; units: Units; unitType: T } & { [type in T]: number })[]
        );
    }, [sensorData, type]);

    return { data, ...response };
};

export const useDissolvedOxygenData = (options: UseHistoricalSensorDataOptions) => {
    const { data: sensorData, ...response } = useHistoricalSensorData(options);

    const data = useMemo(() => {
        if (!sensorData) return undefined;

        return mapValues(sensorData, (data) =>
            compact(
                data.map((datum): DissolvedOxygenDatum | null =>
                    'bm_do_mg_per_l_mean_17bits' in datum.data
                        ? {
                              timestamp: datum.timestamp,
                              ...renameSensorDatumKeys(datum, 'bm_do_mg_per_l_mean_17bits', 'concentration'),
                              ...renameSensorDatumKeys(datum, 'bm_do_saturation_pct_mean_18bits', 'saturation'),
                              ...renameSensorDatumKeys(datum, 'bm_do_quality_mean_10bits', 'quality'),
                          }
                        : null
                )
            )
        );
    }, [sensorData]);

    return { data, ...response };
};

export const useSalinityData = (options: UseHistoricalSensorDataOptions) => {
    const { data: sensorData, ...response } = useHistoricalSensorData(options);

    const data = useMemo(() => {
        if (!sensorData) return undefined;

        return mapValues(sensorData, (data) =>
            compact(
                data.map((datum): SalinityDatum | null =>
                    'bm_aanderaa_salinity_mean_16bits' in datum.data
                        ? {
                              timestamp: datum.timestamp,
                              salinity: datum.data['bm_aanderaa_salinity_mean_16bits'].value,
                              salinityStdDev: datum.data['bm_aanderaa_salinity_std_8bits']?.value,
                              units: datum.data['bm_aanderaa_salinity_mean_16bits'].units as SalinityUnits,
                              unitType: 'salinity',
                          }
                        : null
                )
            )
        );
    }, [sensorData]);

    return { data, ...response };
};
